import React from 'react';
import { Box, Modal, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { TOOLTIPS, TooltipKey } from '../compareData';
import { CheckIcon } from '../CompareIcons';
import { GRAY } from '../constants';

interface TooltipModalProps {
  tooltipKey: TooltipKey | null;
  onClose: () => void;
}

/** Feature explainer modal (opened by an attribute's info icon). */
export const TooltipModal: React.FC<TooltipModalProps> = ({ tooltipKey, onClose }) => {
  const { t } = useTranslation();
  const tooltip = tooltipKey ? TOOLTIPS[tooltipKey] : null;
  return (
    <Modal
      opened={tooltipKey !== null}
      onClose={onClose}
      centered
      radius={24}
      size={420}
      padding={32}
      title={tooltip ? t(tooltip.titleKey) : undefined}
      closeButtonProps={{ 'aria-label': t('compare_v2_close') }}
      // `padding` pads the header all round; trim its bottom so the title stays tight to the body.
      styles={{ title: { fontSize: 22, fontWeight: 700 }, header: { paddingBottom: 8 } }}
    >
      {tooltip && (
        <>
          <Text c={GRAY} fz={15} mb={20} lh={1.5}>
            {t(tooltip.introKey)}
          </Text>
          <Box component="ul" m={0} p={0} sx={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
            {tooltip.items.map((item) => (
              <Box component="li" key={item.titleKey} sx={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <Box
                  bg="#c9f0d5"
                  w={22}
                  h={22}
                  mt={1}
                  sx={{
                    flex: 'none',
                    borderRadius: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& svg': { width: 13, height: 13 },
                  }}
                >
                  <CheckIcon />
                </Box>
                <Box>
                  <Text fz={16} fw={600} lh={1.2}>
                    {t(item.titleKey)}
                  </Text>
                  <Text fz={13} c={GRAY} mt={1} lh={1.2}>
                    {t(item.descKey)}
                  </Text>
                </Box>
              </Box>
            ))}
          </Box>
          {tooltip.outroKey && (
            <Text mt={20} fz={14} c={GRAY} lh={1.5}>
              {t(tooltip.outroKey)}
            </Text>
          )}
        </>
      )}
    </Modal>
  );
};
